import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, ArrowLeft, Shield, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { TOTPEnrollment } from '@/components/mfa/TOTPEnrollment';
import { TOTPVerification } from '@/components/mfa/TOTPVerification';
import { useMFA } from '@/hooks/useMFA';
import { platformAdminValidator } from '@/utils/platformAdminValidation';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';

export default function SecureAdminPortal() {
  const { t } = useTranslation(['auth', 'common', 'platformAdmin']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [authStep, setAuthStep] = useState<'login' | 'mfa_enroll' | 'mfa_verify' | 'authenticated'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isEnrolled, checkMFAStatus } = useMFA();
  const navigationRef = useRef(false);

  const handleForgotPassword = async () => {
    if (!resetEmail) return;
    setResetSending(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        toast({ title: t('auth:unexpectedError'), description: error.message, variant: 'destructive' });
      } else {
        toast({
          title: t('auth:checkEmail'),
          description: t('auth:resetPasswordDescription'),
        });
        setShowForgotPassword(false);
        setResetEmail('');
      }
    } catch (err) {
      toast({ title: t('auth:unexpectedError'), variant: 'destructive' });
    } finally {
      setResetSending(false);
    }
  };

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        const validationResult = await platformAdminValidator.validatePlatformAdmin(session.user.id);

        if (validationResult === 'valid') {
          // Get admin record from database to check mfa_required flag
          const { data: adminRecord } = await supabase
            .from('platform_admin_users')
            .select('mfa_required')
            .eq('user_id', session.user.id)
            .single();

          // If MFA is not required in database, bypass MFA checks
          if (adminRecord && !adminRecord.mfa_required) {
            setAuthStep('authenticated');
            navigationRef.current = true;
            navigate('/platform-admin');
            return;
          }

          // Check MFA status and get the result directly
          const mfaStatus = await checkMFAStatus();

          // If MFA endpoints are unreachable, allow navigation to platform admin
          if (!mfaStatus.reachable) {
            console.warn('MFA endpoints unreachable, allowing admin access');
            setAuthStep('authenticated');
            navigationRef.current = true;
            navigate('/platform-admin');
            return;
          }

          if (mfaStatus.enrolled && mfaStatus.verified) {
            setAuthStep('authenticated');
            navigationRef.current = true;
            navigate('/platform-admin');
          } else if (mfaStatus.enrolled) {
            // Factor exists but is UNVERIFIED (enrollment abandoned, then the page
            // reloaded with the session still alive). Route to enrollment, NOT mfa_verify:
            // challengeMFA needs a verified factor and would dead-end here, whereas
            // TOTPEnrollment surfaces the working "clean up and restart" path. Matches
            // handlePlatformAdminLogin's routing for the same enrolled-but-unverified state.
            setAuthStep('mfa_enroll');
          } else {
            setAuthStep('mfa_enroll');
          }
        } else if (validationResult === 'unknown') {
          // Show retry option for unknown status
          setError(t('auth:unableToVerifyAccess'));
        }
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setError(t('auth:connectionError'));
    }
  };

  useEffect(() => {
    // Only run platform admin validation on mount, not on every user/session change
    if (!navigationRef.current) {
      checkAuth();
    }
  }, []);

  const handlePlatformAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // First, authenticate the user
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      });

      if (authError) {
        setError(authError.message);
        return;
      }

      if (!data.user) {
        setError(t('auth:authenticationFailed'));
        return;
      }

      // Validate platform admin session with the new tri-state function
      const validationResult = await platformAdminValidator.validatePlatformAdmin(data.user.id);

      if (validationResult === 'invalid') {
        await supabase.auth.signOut();
        setError(t('auth:accessDeniedAdmin'));
        return;
      } else if (validationResult === 'unknown') {
        await supabase.auth.signOut();
        setError(t('auth:unableToVerifyAccess'));
        return;
      }

      // Get admin record from database to check mfa_required flag
      const { data: adminRecord } = await supabase
        .from('platform_admin_users')
        .select('mfa_required')
        .eq('user_id', data.user.id)
        .single();

      // If MFA is not required in database, bypass MFA checks
      if (adminRecord && !adminRecord.mfa_required) {
        setAuthStep('authenticated');
        navigationRef.current = true;
        navigate('/platform-admin');
        return;
      }

      // Check MFA enrollment status and get the result directly
      const mfaStatus = await checkMFAStatus();
      
      if (!mfaStatus.enrolled) {
        setAuthStep('mfa_enroll');
        toast({
          title: t('auth:mfaSetupRequiredTitle'),
          description: t('auth:mfaSetupRequiredDesc'),
        });
      } else if (mfaStatus.enrolled && !mfaStatus.verified) {
        // Handle case where factor exists but is unverified
        setAuthStep('mfa_enroll');
        toast({
          title: t('auth:mfaSetupIncompleteTitle'),
          description: t('auth:mfaSetupIncompleteDesc'),
        });
      } else {
        setAuthStep('mfa_verify');
      }
    } catch (error) {
      console.error('Platform admin login error:', error);
      setError(t('auth:loginUnexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFAEnrollmentComplete = () => {
    setAuthStep('authenticated');
    navigationRef.current = true;
    toast({
      title: t('auth:mfaSetupCompleteTitle'),
      description: t('auth:mfaSetupCompleteDesc'),
    });
    navigate('/platform-admin');
  };

  const handleMFAVerificationComplete = () => {
    setAuthStep('authenticated');
    navigationRef.current = true;
    toast({
      title: t('auth:accessGrantedTitle'),
      description: t('auth:accessGrantedDesc'),
    });
    navigate('/platform-admin');
  };

  const handleMFAReset = () => {
    setAuthStep('mfa_enroll');
    toast({
      title: t('auth:mfaResetCompleteTitle'),
      description: t('auth:mfaResetCompleteDesc'),
    });
  };

  const handleRetry = () => {
    setError(null);
    checkAuth();
  };

  if (authStep === 'mfa_enroll') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-gray-900 p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('platformAdmin:portal.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('auth:mfaRequired')}</p>
          </div>

          <TOTPEnrollment
            onEnrollmentComplete={handleMFAEnrollmentComplete}
            required={true}
          />
        </div>
      </div>
    );
  }

  if (authStep === 'mfa_verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-gray-900 p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('platformAdmin:portal.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('auth:verifyIdentity')}</p>
          </div>

          <TOTPVerification
            onVerificationComplete={handleMFAVerificationComplete}
            onMFAReset={handleMFAReset}
            title={t('auth:adminVerification')}
            description={t('auth:enterCodeAccess')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-gray-900">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('platformAdmin:portal.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('platformAdmin:portal.audience')}</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handlePlatformAdminLogin} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {error}
                    {(error.includes('connection') || error.includes('Failed to fetch')) && (
                      <div className="mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRetry}
                          className="h-8"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          {t('auth:retryConnection')}
                        </Button>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth:email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('platformAdmin:portal.emailPlaceholder')}
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <Label htmlFor="password">{t('auth:password')}</Label>
                  <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="link"
                        className="self-start px-0 py-1 h-auto text-xs text-primary underline sm:self-auto"
                      >
                        {t('auth:forgotPassword')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('auth:resetPasswordTitle')}</DialogTitle>
                        <DialogDescription>
                          {t('auth:resetPasswordDescription')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label htmlFor="admin-reset-email">{t('auth:email')}</Label>
                          <Input
                            id="admin-reset-email"
                            type="email"
                            placeholder={t('auth:adminEmailPlaceholder')}
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={handleForgotPassword}
                          className="w-full"
                          disabled={resetSending || !resetEmail}
                        >
                          {resetSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          {t('auth:sendResetLink')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth:passwordPlaceholder')}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('auth:hidePassword') : t('auth:showPassword')}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth:authenticating')}
                  </>
                ) : (
                  t('platformAdmin:portal.signIn')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/platform-admin')}
            className="text-sm text-gray-700 dark:text-gray-300"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('auth:backToMainSite')}
          </Button>
        </div>
      </div>
    </div>
  );
}